import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

/**
 * Socket type for arrays of CORE_DOCUMENT entity refs. Used by
 * split_document_v2 per-classification sockets so the fanout engine + the
 * editor know exactly what kind of array flows through.
 */
const ARRAY_OF_CORE_DOCUMENT_REFS: SocketType =
  `array<entity_reference:${SystemEntity.CORE_DOCUMENT}>` as SocketType;

/** Socket ids reserved by `split_document_v2`; cannot be used as user classification ids. */
export const SPLIT_DOCUMENT_V2_RESERVED_SOCKET_IDS = ["all_documents"] as const;

/**
 * Generate output sockets for the split_document_v2 step.
 *
 * One array socket per configured classification (id = classification.id),
 * plus a reserved `all_documents` socket that always exists carrying rich
 * `{ document, classificationId, classificationType, confidence? }` objects.
 *
 * With no classifications configured, returns just the reserved `all_documents`
 * socket so the user always sees the catch-all output even before adding any.
 */
export function generateSplitDocumentV2OutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const classifications = configEntryList<{ id?: unknown }>(
    config.customClassifications,
  );

  const { sockets: perClassificationSockets } = buildDynamicSockets({
    reserved: SPLIT_DOCUMENT_V2_RESERVED_SOCKET_IDS,
    version: effectiveSocketIdVersion("split_document_v2", config),
    entries: classifications
      .map((c) => c.id)
      // The id shape is a config rule, not a collision rule, so it applies at
      // every version.
      .filter(
        (id): id is string =>
          typeof id === "string" && /^[a-z][a-z0-9_]*$/.test(id),
      )
      .map((id) => ({
        name: id,
        socket: {
          name: prettify(id),
          type: ARRAY_OF_CORE_DOCUMENT_REFS,
          required: false,
          description: `Child documents classified as "${id}"`,
        },
      })),
  });

  return [
    ...perClassificationSockets,
    {
      id: "all_documents",
      name: "All Documents",
      // Rich objects { document, classificationId, classificationType, confidence? }
      // so use array<json> rather than array<entity_reference>.
      type: "array<json>" as SocketType,
      required: false,
      description: "All child documents with their classification tags",
    },
  ];
}
